/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Switch, Route } from 'react-router';
import { useTranslation } from 'react-i18next';
import { I18nNamespaces } from '../../app/shared/utils/constants.util';
import { usePopupChannels, getFlowSnapshot } from '../../app/shared/utils/popup.util';
import useSecuritySetupFlow from '../../app/shared/hooks/useSecuritySetupFlow';
import Enroll from './Enroll';
import SecuritySetup from './SecuritySetup';
import VerifyMethod from './VerifyMethod';
import GlobalReduxToast from '../../app/shared/components/GlobalReduxToast/GlobalReduxToast';
import LoaEnrichmentResult from './LoaIntegrations/LoaEnrichmentResult/LoaEnrichmentResult';
import NonSsoLoaCallback from './LoaIntegrations/NonSSOLoaCallback/NonSSOLoaCallback';
import LoaProviders from './LoaIntegrations/LoaProviders/LoaProviders';
import MissingParamsErrorPage from './MissingParamsErrorPage/MissingParamsErrorPage';
import { resetSetupSecurityState } from './setupSecuritySlice';
import { getConfig } from '../../app/shared/utils/config.util';
import { SecuritySetupRoutes } from '../../app/shared/utils/securitySetupRoutes';

const SecuritySetupShell = () => {
  usePopupChannels();
  useSecuritySetupFlow();
  const dispatch = useDispatch();

  useEffect(() => {
    if (getConfig().NODE_ENV === 'production') return;
    const flow = getFlowSnapshot();
    // eslint-disable-next-line no-console
    console.group('[LoA] SecuritySetup flow params');
    // eslint-disable-next-line no-console
    console.table({
      mode: flow.mode ?? '(defaulted to popup)',
      loaLevel: flow.loaLevel,
      channelId: flow.channelId ?? '(missing)',
      applicationId: flow.applicationId ?? '(missing)',
      relativePath: flow.relativePath ?? '(missing)',
      instance: flow.instance ?? '(missing)',
      resourceName: flow.resourceName ?? '(none)'
    });
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, []);

  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);

  useEffect(() => {
    document.title = t('loaEnrichment.documentTitle');
  }, [t]);

  useEffect(() => () => {
    dispatch(resetSetupSecurityState());
  }, [dispatch]);

  return (
    <>
      <GlobalReduxToast />
      <Switch>
        <Route exact path={SecuritySetupRoutes.root} component={SecuritySetup} />
        <Route exact path={SecuritySetupRoutes.enrollTemplate} component={Enroll} />
        <Route exact path={SecuritySetupRoutes.verifyTemplate} component={VerifyMethod} />
        <Route exact path={SecuritySetupRoutes.providers} component={LoaProviders} />
        <Route exact path={SecuritySetupRoutes.enrichment} component={LoaEnrichmentResult} />
        <Route exact path={SecuritySetupRoutes.error} component={MissingParamsErrorPage} />
        <Route exact path={SecuritySetupRoutes.loaCallback} component={NonSsoLoaCallback} />
      </Switch>
    </>
  );
};

export default SecuritySetupShell;
